/**
 * Pixel operations on RGBA buffers (§19 image editing).
 *
 * Deliberately free of any decoder, canvas or DOM. The platform decodes to
 * RGBA — the browser, `ImageDecoder`, `CIImage` — and hands the buffer here, so
 * the arithmetic that actually changes someone's photo is testable pixel by
 * pixel rather than only visible in a screenshot.
 *
 * Every operation returns a new buffer. §13 requires the original to survive
 * editing, and an in-place operation is one aliasing bug away from destroying
 * the source image the undo stack points at.
 */

export interface RgbaImage {
  readonly width: number;
  readonly height: number;
  /** Row-major RGBA, 4 bytes per pixel, length === width * height * 4. */
  readonly data: Uint8ClampedArray;
}

export class ImageOpError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageOpError';
  }
}

export function createImage(width: number, height: number): RgbaImage {
  if (width <= 0 || height <= 0 || !Number.isInteger(width) || !Number.isInteger(height)) {
    throw new ImageOpError(`invalid dimensions: ${width}×${height}`);
  }
  return { width, height, data: new Uint8ClampedArray(width * height * 4) };
}

export function assertImage(image: RgbaImage): void {
  const expected = image.width * image.height * 4;
  if (image.data.length !== expected) {
    throw new ImageOpError(
      `buffer length ${image.data.length} does not match ${image.width}×${image.height} (expected ${expected})`,
    );
  }
}

/** Index of a pixel's first byte. */
function idx(image: RgbaImage, x: number, y: number): number {
  return (y * image.width + x) * 4;
}

export function getPixel(image: RgbaImage, x: number, y: number): [number, number, number, number] {
  const i = idx(image, x, y);
  return [image.data[i]!, image.data[i + 1]!, image.data[i + 2]!, image.data[i + 3]!];
}

export function setPixel(
  image: RgbaImage, x: number, y: number, rgba: readonly [number, number, number, number],
): void {
  const i = idx(image, x, y);
  image.data[i] = rgba[0];
  image.data[i + 1] = rgba[1];
  image.data[i + 2] = rgba[2];
  image.data[i + 3] = rgba[3];
}

export function cloneImage(image: RgbaImage): RgbaImage {
  return { width: image.width, height: image.height, data: Uint8ClampedArray.from(image.data) };
}

/* ------------------------------------------------------------------ *
 * geometry
 * ------------------------------------------------------------------ */

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Clamp a rectangle to the image, returning null if nothing is left. */
export function clampRect(image: RgbaImage, rect: Rect): Rect | null {
  const x = Math.max(0, Math.min(Math.floor(rect.x), image.width));
  const y = Math.max(0, Math.min(Math.floor(rect.y), image.height));
  const right = Math.max(0, Math.min(Math.ceil(rect.x + rect.width), image.width));
  const bottom = Math.max(0, Math.min(Math.ceil(rect.y + rect.height), image.height));
  const width = right - x;
  const height = bottom - y;
  return width > 0 && height > 0 ? { x, y, width, height } : null;
}

export function crop(image: RgbaImage, rect: Rect): RgbaImage {
  assertImage(image);
  const area = clampRect(image, rect);
  if (!area) throw new ImageOpError('crop rectangle lies entirely outside the image');

  const out = createImage(area.width, area.height);
  for (let y = 0; y < area.height; y++) {
    const from = idx(image, area.x, area.y + y);
    out.data.set(image.data.subarray(from, from + area.width * 4), y * area.width * 4);
  }
  return out;
}

export type Rotation = 90 | 180 | 270;

/**
 * Rotate by a right angle.
 *
 * Only right angles: arbitrary rotation needs resampling, which softens the
 * image, and a viewer's rotate button is nearly always straightening a photo
 * the camera tagged sideways. Right angles are exact — every pixel survives.
 */
export function rotate(image: RgbaImage, degrees: Rotation): RgbaImage {
  assertImage(image);
  const { width: w, height: h } = image;
  const out = degrees === 180 ? createImage(w, h) : createImage(h, w);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = getPixel(image, x, y);
      if (degrees === 90) setPixel(out, h - 1 - y, x, p);
      else if (degrees === 180) setPixel(out, w - 1 - x, h - 1 - y, p);
      else setPixel(out, y, w - 1 - x, p);
    }
  }
  return out;
}

export function flipHorizontal(image: RgbaImage): RgbaImage {
  assertImage(image);
  const out = createImage(image.width, image.height);
  for (let y = 0; y < image.height; y++) {
    for (let x = 0; x < image.width; x++) {
      setPixel(out, image.width - 1 - x, y, getPixel(image, x, y));
    }
  }
  return out;
}

export function flipVertical(image: RgbaImage): RgbaImage {
  assertImage(image);
  const out = createImage(image.width, image.height);
  for (let y = 0; y < image.height; y++) {
    const from = idx(image, 0, y);
    out.data.set(
      image.data.subarray(from, from + image.width * 4),
      (image.height - 1 - y) * image.width * 4,
    );
  }
  return out;
}

/**
 * Resize with box sampling when shrinking and nearest-neighbour when growing.
 *
 * Box sampling averages every source pixel that falls inside a destination
 * pixel. Nearest-neighbour downscaling — the naive version — throws most of
 * them away and produces the shimmering aliased thumbnails that make a viewer
 * look broken.
 */
export function resize(image: RgbaImage, width: number, height: number): RgbaImage {
  assertImage(image);
  if (width <= 0 || height <= 0) throw new ImageOpError(`invalid size: ${width}×${height}`);
  if (width === image.width && height === image.height) return cloneImage(image);

  const out = createImage(width, height);
  const scaleX = image.width / width;
  const scaleY = image.height / height;
  const shrinking = scaleX > 1 || scaleY > 1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!shrinking) {
        const sx = Math.min(image.width - 1, Math.floor(x * scaleX));
        const sy = Math.min(image.height - 1, Math.floor(y * scaleY));
        setPixel(out, x, y, getPixel(image, sx, sy));
        continue;
      }

      const x0 = Math.floor(x * scaleX);
      const y0 = Math.floor(y * scaleY);
      const x1 = Math.min(image.width, Math.max(x0 + 1, Math.ceil((x + 1) * scaleX)));
      const y1 = Math.min(image.height, Math.max(y0 + 1, Math.ceil((y + 1) * scaleY)));

      let r = 0, g = 0, b = 0, a = 0, n = 0;
      for (let sy = y0; sy < y1; sy++) {
        for (let sx = x0; sx < x1; sx++) {
          const i = idx(image, sx, sy);
          r += image.data[i]!; g += image.data[i + 1]!;
          b += image.data[i + 2]!; a += image.data[i + 3]!;
          n++;
        }
      }
      setPixel(out, x, y, [r / n, g / n, b / n, a / n]);
    }
  }
  return out;
}

/** Largest size that fits inside a box, preserving aspect ratio. */
export function fitWithin(
  width: number, height: number, maxWidth: number, maxHeight: number,
): { width: number; height: number } {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/* ------------------------------------------------------------------ *
 * concealment (§19 "Mosaic", "Blur")
 * ------------------------------------------------------------------ */

/**
 * Pixelate a region by averaging each block.
 *
 * The averaging is what makes this irreversible. A mosaic drawn as an overlay
 * would leave the original pixels underneath — the image equivalent of the fake
 * PDF redaction §14 forbids — so the source data is replaced, not covered.
 */
export function mosaic(image: RgbaImage, rect: Rect, blockSize: number): RgbaImage {
  assertImage(image);
  if (blockSize < 2) throw new ImageOpError('mosaic block size must be at least 2');
  const area = clampRect(image, rect);
  if (!area) throw new ImageOpError('mosaic rectangle lies entirely outside the image');

  const out = cloneImage(image);
  for (let by = area.y; by < area.y + area.height; by += blockSize) {
    for (let bx = area.x; bx < area.x + area.width; bx += blockSize) {
      const x1 = Math.min(bx + blockSize, area.x + area.width);
      const y1 = Math.min(by + blockSize, area.y + area.height);

      let r = 0, g = 0, b = 0, a = 0, n = 0;
      for (let y = by; y < y1; y++) {
        for (let x = bx; x < x1; x++) {
          const i = idx(image, x, y);
          r += image.data[i]!; g += image.data[i + 1]!;
          b += image.data[i + 2]!; a += image.data[i + 3]!;
          n++;
        }
      }
      const avg: [number, number, number, number] = [r / n, g / n, b / n, a / n];
      for (let y = by; y < y1; y++) {
        for (let x = bx; x < x1; x++) setPixel(out, x, y, avg);
      }
    }
  }
  return out;
}

/**
 * Box blur over a region, applied `passes` times.
 *
 * Three passes of a box blur approximate a Gaussian closely enough that the
 * difference is invisible, at a fraction of the cost — and unlike a true
 * Gaussian it needs no kernel table, which keeps this dependency-free.
 */
export function blur(image: RgbaImage, rect: Rect, radius: number, passes = 3): RgbaImage {
  assertImage(image);
  if (radius < 1) throw new ImageOpError('blur radius must be at least 1');
  const area = clampRect(image, rect);
  if (!area) throw new ImageOpError('blur rectangle lies entirely outside the image');

  let out = cloneImage(image);
  for (let pass = 0; pass < passes; pass++) {
    const source = out;
    const next = cloneImage(source);
    for (let y = area.y; y < area.y + area.height; y++) {
      for (let x = area.x; x < area.x + area.width; x++) {
        let r = 0, g = 0, b = 0, a = 0, n = 0;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            // Sampling is clamped to the *image*, not the region, so the edge
            // of a blurred area blends with what surrounds it instead of
            // showing a hard seam.
            const sx = Math.max(0, Math.min(image.width - 1, x + dx));
            const sy = Math.max(0, Math.min(image.height - 1, y + dy));
            const i = idx(source, sx, sy);
            r += source.data[i]!; g += source.data[i + 1]!;
            b += source.data[i + 2]!; a += source.data[i + 3]!;
            n++;
          }
        }
        setPixel(next, x, y, [r / n, g / n, b / n, a / n]);
      }
    }
    out = next;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * adjustments (§19)
 * ------------------------------------------------------------------ */

export interface Adjustments {
  /** −100…100, 0 = unchanged. */
  readonly brightness?: number;
  readonly contrast?: number;
  readonly saturation?: number;
}

/** Rec. 709 luma, the weighting that matches how eyes perceive brightness. */
function luma(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function adjust(image: RgbaImage, options: Adjustments): RgbaImage {
  assertImage(image);
  const brightness = (options.brightness ?? 0) / 100;
  const contrast = (options.contrast ?? 0) / 100;
  const saturation = (options.saturation ?? 0) / 100;

  // Standard contrast curve: maps −1…1 onto a multiplier that pivots at mid-grey.
  const contrastFactor = (1.015 * (contrast + 1)) / (1.015 - contrast);
  const out = cloneImage(image);

  for (let i = 0; i < out.data.length; i += 4) {
    let r = out.data[i]!;
    let g = out.data[i + 1]!;
    let b = out.data[i + 2]!;

    if (brightness !== 0) {
      const shift = brightness * 255;
      r += shift; g += shift; b += shift;
    }
    if (contrast !== 0) {
      r = contrastFactor * (r - 128) + 128;
      g = contrastFactor * (g - 128) + 128;
      b = contrastFactor * (b - 128) + 128;
    }
    if (saturation !== 0) {
      const l = luma(r, g, b);
      const s = saturation + 1;
      r = l + (r - l) * s;
      g = l + (g - l) * s;
      b = l + (b - l) * s;
    }

    // Uint8ClampedArray clamps for us, which is exactly the behaviour wanted:
    // an over-bright pixel saturates to white rather than wrapping to black.
    out.data[i] = r;
    out.data[i + 1] = g;
    out.data[i + 2] = b;
  }
  return out;
}

export function grayscale(image: RgbaImage): RgbaImage {
  assertImage(image);
  const out = cloneImage(image);
  for (let i = 0; i < out.data.length; i += 4) {
    const l = luma(out.data[i]!, out.data[i + 1]!, out.data[i + 2]!);
    out.data[i] = l; out.data[i + 1] = l; out.data[i + 2] = l;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * drawing
 * ------------------------------------------------------------------ */

export type Rgba = readonly [r: number, g: number, b: number, a: number];

/** Source-over alpha compositing, the same rule canvas uses. */
function blend(dst: RgbaImage, x: number, y: number, src: Rgba): void {
  if (x < 0 || y < 0 || x >= dst.width || y >= dst.height) return;
  const alpha = src[3] / 255;
  if (alpha <= 0) return;
  if (alpha >= 1) { setPixel(dst, x, y, src); return; }

  const i = idx(dst, x, y);
  dst.data[i] = src[0] * alpha + dst.data[i]! * (1 - alpha);
  dst.data[i + 1] = src[1] * alpha + dst.data[i + 1]! * (1 - alpha);
  dst.data[i + 2] = src[2] * alpha + dst.data[i + 2]! * (1 - alpha);
  dst.data[i + 3] = Math.max(dst.data[i + 3]!, src[3]);
}

export function fillRect(image: RgbaImage, rect: Rect, color: Rgba): RgbaImage {
  assertImage(image);
  const area = clampRect(image, rect);
  if (!area) return cloneImage(image);
  const out = cloneImage(image);
  for (let y = area.y; y < area.y + area.height; y++) {
    for (let x = area.x; x < area.x + area.width; x++) blend(out, x, y, color);
  }
  return out;
}

export function strokeRect(
  image: RgbaImage, rect: Rect, color: Rgba, thickness = 2,
): RgbaImage {
  assertImage(image);
  let out = cloneImage(image);
  const t = Math.max(1, Math.round(thickness));
  out = fillRect(out, { x: rect.x, y: rect.y, width: rect.width, height: t }, color);
  out = fillRect(out, { x: rect.x, y: rect.y + rect.height - t, width: rect.width, height: t }, color);
  out = fillRect(out, { x: rect.x, y: rect.y, width: t, height: rect.height }, color);
  out = fillRect(out, { x: rect.x + rect.width - t, y: rect.y, width: t, height: rect.height }, color);
  return out;
}

/** Bresenham line, thickened by stamping a square at each step. */
export function drawLine(
  image: RgbaImage,
  from: readonly [number, number],
  to: readonly [number, number],
  color: Rgba,
  thickness = 2,
): RgbaImage {
  assertImage(image);
  const out = cloneImage(image);
  const half = Math.max(0, Math.floor(thickness / 2));

  let x0 = Math.round(from[0]);
  let y0 = Math.round(from[1]);
  const x1 = Math.round(to[0]);
  const y1 = Math.round(to[1]);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;

  for (;;) {
    for (let oy = -half; oy <= half; oy++) {
      for (let ox = -half; ox <= half; ox++) blend(out, x0 + ox, y0 + oy, color);
    }
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
  return out;
}

/** Freehand stroke: a polyline through every captured point. */
export function drawPolyline(
  image: RgbaImage, points: readonly (readonly [number, number])[], color: Rgba, thickness = 3,
): RgbaImage {
  if (points.length === 0) return cloneImage(image);
  if (points.length === 1) return drawLine(image, points[0]!, points[0]!, color, thickness);
  let out = cloneImage(image);
  for (let i = 1; i < points.length; i++) {
    out = drawLine(out, points[i - 1]!, points[i]!, color, thickness);
  }
  return out;
}

/** Composite one image onto another at an offset — used to place stamps and signatures. */
export function compositeOver(
  base: RgbaImage, overlay: RgbaImage, x: number, y: number, opacity = 1,
): RgbaImage {
  assertImage(base);
  assertImage(overlay);
  const out = cloneImage(base);
  for (let oy = 0; oy < overlay.height; oy++) {
    for (let ox = 0; ox < overlay.width; ox++) {
      const [r, g, b, a] = getPixel(overlay, ox, oy);
      blend(out, x + ox, y + oy, [r, g, b, a * opacity]);
    }
  }
  return out;
}
